import { BookingLink } from "../components/BookingLink.tsx";
import { Reveal } from "../components/Reveal.tsx";
import { STUDIO } from "../lib/studio.ts";
import { dayName, trimTime } from "../lib/format.ts";
import type { OpeningWindowRecord, StudioResponse } from "../lib/api.ts";

interface VisitProps {
  studio: StudioResponse | null;
}

/** Monday-first, which is how a UK opening-hours list is read. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const FALLBACK = {
  "visit.eyebrow": "Your visit",
  "visit.title": "Finding us",
  "visit.lede":
    "A private home studio in Finchampstead, near Wokingham, with parking outside.",
  "visit.addressNote": "The full address comes with your booking confirmation.",
};

interface HoursRow {
  days: string;
  /** One entry per opening window; empty when the studio is closed. */
  windows: string[];
}

/**
 * The week as a handful of rows rather than seven.
 *
 * A run of three or more days with the same hours collapses to "Mon to Fri".
 * Two alike stay as two rows: "Sat to Sun" says less than "Saturday" and
 * "Sunday" do, and costs nearly as much room.
 */
function hoursRows(windows: OpeningWindowRecord[]): HoursRow[] {
  const days = DAY_ORDER.map((dayOfWeek) => ({
    dayOfWeek,
    windows: windows
      .filter((entry) => entry.dayOfWeek === dayOfWeek)
      .sort((a, b) => a.openTime.localeCompare(b.openTime))
      .map((entry) => `${trimTime(entry.openTime)} to ${trimTime(entry.closeTime)}`),
  }));

  const runs: (typeof days)[] = [];
  for (const day of days) {
    const run = runs.at(-1);
    if (run && run[0]!.windows.join() === day.windows.join()) run.push(day);
    else runs.push([day]);
  }

  return runs.flatMap((run) => {
    if (run.length < 3) {
      return run.map((day) => ({ days: dayName(day.dayOfWeek), windows: day.windows }));
    }
    const first = dayName(run[0]!.dayOfWeek).slice(0, 3);
    const last = dayName(run.at(-1)!.dayOfWeek).slice(0, 3);
    return [{ days: `${first} to ${last}`, windows: run[0]!.windows }];
  });
}

export function Visit({ studio }: VisitProps) {
  const content = studio?.content ?? {};
  const text = (key: keyof typeof FALLBACK) => content[key] ?? FALLBACK[key];

  return (
    <section className="section" id="visit">
      <div className="page">
        <Reveal className="visit">
          <div className="visit__column">
            <p className="eyebrow">{text("visit.eyebrow")}</p>
            <h2 className="visit__title">{text("visit.title")}</h2>
            {/* Deliberately not the street: see the note in lib/studio.ts. */}
            <p className="visit__text">
              {STUDIO.addressLines.join(", ")}. {text("visit.lede")}{" "}
              {text("visit.addressNote")}
            </p>
            <a
              href={STUDIO.mapsUrl}
              className="text-link"
              target="_blank"
              rel="noreferrer noopener"
            >
              Open in Maps
            </a>
          </div>

          <div className="visit__column">
            <h3 className="visit__heading">Opening hours</h3>
            {!studio ? (
              <p className="visit__text">Loading…</p>
            ) : (
              <dl className="hours-list">
                {hoursRows(studio.windows).map((row) => (
                  <div key={row.days} className="hours-list__row">
                    <dt>{row.days}</dt>
                    <dd
                      className={
                        row.windows.length === 0 ? "hours-list__closed" : undefined
                      }
                    >
                      {row.windows.length === 0
                        ? "Closed"
                        : row.windows.map((hours) => (
                            <span key={hours} className="hours-list__window">
                              {hours}
                            </span>
                          ))}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="visit__column">
            <h3 className="visit__heading">Get in touch</h3>
            <a className="visit__contact" href={`tel:${STUDIO.phoneHref}`}>
              {STUDIO.phoneDisplay}
            </a>
            <a
              className="visit__contact"
              href={`https://instagram.com/${STUDIO.instagram}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              @{STUDIO.instagram}
            </a>
            <BookingLink className="btn visit__book">
              Check availability
            </BookingLink>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
